import { useState, useEffect, useMemo, useRef } from "react";
import { db } from "firebaseConfig";
import useAuth from "hooks/useAuth";
import { GenericConverter } from "utils/firestoreConverters";
import { postProcessContact } from "utils/contacts/helper";
import { getPhoneCandidates } from "components/_dashboard/listings/seller/sellerPhone";
import { CONTACT_DOCUMENT_SOURCE } from "./contactDocumentSource";
import { contactDocumentStore } from "./contactDocumentStore";
import { decideCandidatesAsk, hasLiveCandidateNews, resolveCandidates } from "./contactDocumentRules";
import { subscribeCandidateSignals } from "./contactSignals";
import { subscribeTabVisible } from "./tabVisibility";
import { useContactDocumentShadow } from "./contactDocumentShadow";
import useContactBook from "./useContactBook";
import useContactDocument, { fetchContactCandidates, useContactDocumentStoreSubscription } from "./useContactDocument";
import type { ShadowServerAnswer } from "./contactDocumentShadow";
import type { RealEstateContact } from "../../@types/sharedSchema";

export type ContactByPhone = {
  /** `undefined` = still resolving. Callers must not read this as "no". */
  exists: boolean | undefined;
  /** The live document. Undefined while resolving and when there is no such contact. */
  contact: RealEstateContact | undefined;
  hasError: boolean;
};

const RESOLVING: ContactByPhone = { exists: undefined, contact: undefined, hasError: false };
const NO_CONTACT: ContactByPhone = { exists: false, contact: undefined, hasError: false };

/**
 * ONE contact, LIVE and WHOLE — the document for a phone in the own book of the caller.
 *
 * ── When to use this, and when to use `useContactLookup` ────────────────────────────
 *
 * A LABEL next to a phone (a name in a table cell, a chip, a list of signers) is
 * `useContactLookup` / `useContactsLookup`: a batched read of the Postgres mirror that
 * returns a PROJECTION of the contact — about thirty fields.
 *
 * A CARD OR A DIALOG ABOUT ONE CONTACT is this hook. It reads the document itself, which
 * carries every field. Two reasons that is not interchangeable:
 *
 *  - an edit form opened on a projection writes the projection back — every field the
 *    mirror does not carry is gone from the document;
 *  - the answer here decides between LINKING an existing contact and CREATING one. The
 *    mirror is a moment behind the document; the document is never behind itself.
 *
 * It is live, so a card that is open while a call is transcribed or an automation writes
 * the contact repaints on its own.
 *
 * ── Which mechanism answers: `CONTACT_DOCUMENT_SOURCE` ──────────────────────────────
 *
 * Today's answer is a Firestore listener on the document. The record is moving to
 * Postgres (`contactDocumentSource.ts`), where the answer becomes the document store:
 * `POST /contacts/get` with every spelling of the phone — the server says which one
 * exists — then `useContactDocument` on that id, which refetches when the contact's signal
 * document says a newer version exists. In 'shadow' mode both run and the listener
 * answers; each time both have a definite answer they are compared and the server side's
 * latency is timed (`contactDocumentShadow.ts`). The contract of this hook is the same in
 * every mode.
 *
 * ── Spelling-tolerant ───────────────────────────────────────────────────────────────
 *
 * Asked by every plausible document id the phone could be stored under (see
 * `getPhoneCandidates`). Ids should all be E.164 and nearly all are, but a lookup that tries
 * only the canonical form reports "not a contact" for the few that are not — and the caller
 * then creates a duplicate.
 *
 * ── The answer belongs to the phone it was asked about ──────────────────────────────
 *
 * It is stored WITH its phone, and a render that asks about a different phone gets
 * "resolving" in that very render. It used to be reset from an effect, which runs after
 * the paint: for one frame after the phone changed, the hook still returned the answer to
 * the PREVIOUS phone. A dialog kept mounted with an empty phone and then handed a real one
 * painted "not a contact — add?" for that frame, and anything that acted on it would have
 * created a contact that exists. (The store-backed answer is derived in the render from the
 * current phone's candidates, so it cannot belong to another phone.)
 *
 * ── `enabled` ───────────────────────────────────────────────────────────────────────
 *
 * For virtualised rows: a row scrolled out of view unsubscribes but keeps its last answer,
 * so scrolling back does not flash a spinner. A caller that is not in a list omits it.
 *
 * ⚠ `exists === undefined` means "still resolving", not "no". Anything that WRITES on the
 * strength of this must wait for a definite answer, or it creates a contact that exists.
 */
export default function useContactByPhone(phoneNumber: string | null | undefined, enabled = true): ContactByPhone {
  const { user } = useAuth();
  const userEmail = user?.email as string | undefined;
  const phone = phoneNumber || null;

  const listener = useFirestoreContactByPhone(phone, enabled && CONTACT_DOCUMENT_SOURCE !== "server", userEmail);
  const server = useStoredContactByPhone(phone, enabled && CONTACT_DOCUMENT_SOURCE !== "firestore", userEmail);

  useContactDocumentShadow(
    "useContactByPhone",
    listener.exists === undefined ? undefined : { exists: listener.exists, doc: listener.contact ?? null },
    server.answer.exists === undefined
      ? undefined
      : { exists: server.answer.exists, doc: server.answer.contact ?? null, lastFetch: server.lastFetch }
  );

  // No phone is a definite answer: there is nobody to be a contact.
  if (!phone) return NO_CONTACT;

  return CONTACT_DOCUMENT_SOURCE === "server" ? server.answer : listener;
}

/** Today's mechanism: a snapshot listener on the document, by every spelling of the phone. */
function useFirestoreContactByPhone(
  phone: string | null,
  enabled: boolean,
  userEmail: string | undefined
): ContactByPhone {
  const [answer, setAnswer] = useState<{ phone: string; result: ContactByPhone } | null>(null);

  useEffect(() => {
    // Disabled (a row scrolled out of view): unsubscribed by the cleanup below, and the
    // last answer for this phone is kept.
    if (!phone || !enabled || !userEmail) return undefined;

    const candidates = getPhoneCandidates(phone);

    const unsubscribe = db
      .collection("dashboard-users-v2")
      .doc(userEmail)
      .collection("contacts")
      .withConverter(GenericConverter<RealEstateContact>())
      .where("__name__", "in", candidates)
      .limit(1)
      .onSnapshot(
        (snapshot) => {
          setAnswer({
            phone,
            result: snapshot.empty
              ? NO_CONTACT
              : { exists: true, hasError: false, contact: postProcessContact(snapshot.docs[0].data(), userEmail) }
          });
        },
        (error) => {
          console.error("Failed to listen to contacts:", error);
          // Keep the last known answer for THIS phone rather than falling back to "not a
          // contact": a transient listener error must never be the reason a duplicate
          // contact gets created.
          setAnswer((current) => ({
            phone,
            result: { ...(current?.phone === phone ? current.result : RESOLVING), hasError: true }
          }));
        }
      );

    return () => {
      unsubscribe();
    };
  }, [enabled, phone, userEmail]);

  if (!phone) return NO_CONTACT;
  return answer?.phone === phone ? answer.result : RESOLVING;
}

/**
 * The store-backed mechanism: the phone's candidates resolved through `POST /contacts/get`
 * (the store remembers the answer per spelling), then the found id kept current by
 * `useContactDocument`. The answer is DERIVED from the store in the render, so it always
 * belongs to the phone being asked about.
 */
function useStoredContactByPhone(
  phone: string | null,
  enabled: boolean,
  userEmail: string | undefined
): { answer: ContactByPhone; lastFetch: ShadowServerAnswer["lastFetch"] } {
  // The store belongs to the ACCOUNT. Bound here, BEFORE the store is read below: the first
  // frame after a sign-in must not resolve against the previous user's entries.
  const account = userEmail ?? null;
  contactDocumentStore.bindAccount(account);
  // The own BOOK is the server's answer — the email for a personal book, the office's `org:`
  // key for a pooled member — and the scope is `mine` under it. STRICT (null until known):
  // a pooled member's cards wait one round trip on a cold browser rather than ask under a
  // book with no documents in it, and a personal realtor's answer is persisted, so after
  // the first session nothing waits.
  const { bookKey } = useContactBook();
  const candidates = useMemo(() => (phone ? getPhoneCandidates(phone) : []), [phone]);
  const addressed = Boolean(phone && bookKey);
  // Re-renders this card when an entry of ITS phone's spellings changes (the found contact's
  // is one of them), never on another card's answer: on the listings board that was 200
  // badges re-rendered per answer. Before the ask below, so its effect subscribes first.
  useContactDocumentStoreSubscription(bookKey, candidates);

  const entryOf = (contactId: string) => (bookKey ? contactDocumentStore.get(bookKey, contactId) : undefined);
  const resolution = addressed ? resolveCandidates(candidates, entryOf, Date.now()) : ({ kind: "resolving" } as const);
  const epoch = contactDocumentStore.epoch();
  const candidatesKey = candidates.join("|");

  // ── When the phone is asked about ────────────────────────────────────────────────
  //
  // A PROMPT is: the mount, an invalidation (`epoch`), the tab coming back (`wake`), the
  // contact that was found going away (`contactId`), and news that a contact now exists
  // under one of the spellings (`news`, from the watch further down). On a prompt the phone
  // is asked about when nothing definite is held, when "not a contact" has gone stale, or
  // when there is news no request has answered (`decideCandidatesAsk`). Deliberately NOT
  // "whenever the store says asking is possible": a failed request releases its spellings,
  // which would make that true again in the same tick, and a server that is down would be
  // asked in a tight loop. A failure waits for the next prompt, as in `useContactsLookup`;
  // meanwhile the answer carries `hasError`, which is what the listener reports on an error.
  const askKey = addressed && enabled ? `${bookKey}\n${epoch}\n${candidatesKey}` : null;
  const [wake, setWake] = useState(0);
  // Every card on the page is told at once (`subscribeTabVisible`), so the phones they ask
  // about again leave together — a listener per card sent a board's 200 one by one.
  useEffect(() => {
    if (!askKey) return undefined;
    return subscribeTabVisible(() => setWake((value) => value + 1));
  }, [askKey]);

  // The spelling a document is held under, if any — and one of the prompts: the moment it
  // goes away (a delete in this browser, a signal that says the contact is gone). The
  // spellings after it were never checked by the server (`recordCandidates` forgets them),
  // so nothing definite is held any more, and without this prompt nothing asks: the card sat
  // on "resolving" until the tab came back, where the listener says "not a contact" at once.
  // It cannot loop: a request that fails leaves this null, exactly as it found it.
  const contactId = resolution.kind === "present" ? resolution.contactId : null;

  // ── Standing back, and news ──────────────────────────────────────────────────────
  //
  // `stoodBack`. A prompt that finds one of the spellings already being asked about (a panel
  // reading the same contact by its id, another card on the same phone, this reader's own
  // earlier request) sends nothing over it, and looks again once nothing is out: if what
  // came back was "no such document" for one spelling, the others were never asked about.
  // It is a ref, checked after every render (this hook renders on every change of an entry
  // of the phone's spellings, which is where that request lands). Derived in the render, as
  // it first was, it never fired: a claim re-renders nobody, so the render that would have
  // seen the request in flight never came. And when
  // what came back is a FAILURE for every spelling, that is the server failing, not a
  // spelling nobody asked about: it waits for a prompt like any failure — or ten cards on
  // one phone would each repeat the request the first one lost.
  //
  // `newsPending`. The watch saw a live contact under one of the spellings, and no request
  // that LEFT after it has answered. A request already on its way read the phone BEFORE the
  // news, so its "nobody" settles nothing: the news stays pending, and the phone is asked
  // about again when that request lands. The news is this READER's and is never handed to
  // the store: `invalidate` moves the epoch every reader depends on, and one new contact
  // then made every card whose answer was a minute old ask the server again.
  const stoodBack = useRef(false);
  const newsPending = useRef(false);
  /** A request of ours that left carrying news is still out. */
  const newsRequestOut = useRef(false);
  const [news, setNews] = useState(0);

  // Another phone, or another book: whatever was pending was about the previous one.
  // Declared BEFORE the prompt below, so on such a change it runs first.
  useEffect(() => {
    stoodBack.current = false;
    newsPending.current = false;
    newsRequestOut.current = false;
  }, [bookKey, candidatesKey]);

  const askAboutThePhone = (): void => {
    if (!askKey || !bookKey) {
      stoodBack.current = false;
      return;
    }
    const ids = candidatesKey.split("|");
    const lookup = (candidateId: string) => contactDocumentStore.get(bookKey, candidateId);
    const held = resolveCandidates(ids, lookup, Date.now());
    // A contact is held: whatever the news was, it is on screen.
    if (held.kind === "present") newsPending.current = false;
    const anyInFlight = ids.some((candidateId) => Boolean(lookup(candidateId)?.inFlight));
    const decision = decideCandidatesAsk(held, anyInFlight, newsPending.current);
    stoodBack.current = decision === "stand_back";
    if (decision !== "ask") return;
    // This request leaves after every piece of news so far: its answer settles them.
    newsRequestOut.current = newsPending.current;
    newsPending.current = false;
    // The own book IS the book asked about, so the request carries no scope (`mine`).
    void fetchContactCandidates(bookKey, ids, bookKey);
  };

  useEffect(() => {
    askAboutThePhone();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askKey, bookKey, candidatesKey, wake, contactId, news]);

  // No dependency list, on purpose: after every render. Two flags are read, and nothing more
  // unless this reader stood back or has a request out that carried news.
  useEffect(() => {
    if (!stoodBack.current && !newsRequestOut.current) return;
    if (candidates.some((candidateId) => entryOf(candidateId)?.inFlight)) return;
    if (newsRequestOut.current) {
      newsRequestOut.current = false;
      // It FAILED: the news is still unanswered. Kept, and asked about on the next prompt.
      if (candidates.some((candidateId) => Boolean(entryOf(candidateId)?.error))) newsPending.current = true;
    }
    if (!stoodBack.current) return;
    if (candidates.length > 0 && candidates.every((candidateId) => Boolean(entryOf(candidateId)?.error))) {
      // The request this reader stood back for failed for every spelling: wait for a prompt.
      stoodBack.current = false;
      return;
    }
    askAboutThePhone();
  });

  // ── A phone that is NOT a contact, watched for the day it becomes one ─────────────
  //
  // With no contact held there is no contact's signal to listen to, and every other prompt
  // is about THIS browser. A card that said "not a contact" kept saying it after a call's
  // analysis, an automation or a colleague created the contact, until the tab came back —
  // where the Firestore listener repaints at once. So while the answer is a definite "not a
  // contact", one listener watches the signals of the phone's spellings, and news of a live
  // one is a prompt for THIS reader (`news`). Not while a contact is held (its own signal is
  // listened to), and not before the first answer (that request is on its way, and a contact
  // created meanwhile is in this listener's first snapshot once it has said "no").
  const watchKey = addressed && enabled && resolution.kind === "absent" ? `${bookKey}\n${candidatesKey}` : null;
  const seenSignals = useRef<Map<string, string>>(new Map());
  useEffect(() => {
    if (!watchKey || !bookKey) return undefined;
    const ids = candidatesKey.split("|");
    return subscribeCandidateSignals(bookKey, ids, (signals) => {
      if (!hasLiveCandidateNews(seenSignals.current, signals)) return;
      newsPending.current = true;
      setNews((value) => value + 1);
    });
  }, [watchKey, bookKey, candidatesKey]);

  // Not named `document`: the visibility listener above reads the global one.
  const byId = useContactDocument(bookKey, contactId, enabled);

  // Post-processed like the listener's answer — on a CLONE, because `postProcessContact`
  // mutates and the stored document is shared by every reader. Memoised on the document
  // object, which the store keeps until the document is replaced.
  const contact = useMemo(
    () => (byId.doc && userEmail ? postProcessContact({ ...byId.doc }, userEmail) : undefined),
    [byId.doc, userEmail]
  );

  const askFailed = candidates.some((candidateId) => Boolean(entryOf(candidateId)?.error));

  let answer: ContactByPhone;
  if (!phone) answer = NO_CONTACT;
  else if (resolution.kind === "present") {
    answer =
      byId.doc === null
        ? // Marked absent since (a delete in this browser, a deleted signal).
          { ...NO_CONTACT, hasError: byId.hasError }
        : { exists: contact ? true : undefined, contact, hasError: byId.hasError };
  } else if (resolution.kind === "absent") answer = { ...NO_CONTACT, hasError: askFailed };
  else answer = { ...RESOLVING, hasError: askFailed };

  return { answer, lastFetch: byId.lastFetch };
}
